import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import Checkbox from "../../../../../../components/Checkbox";
import InputCustom from "../../../../../../components/InputCustom";
import SelectCustom from "../../../../../../components/SelectCustom";
import Loading from "../../../../../../core/common/Loading";
import api from "../../../../../../services/api";
import { IParametro } from "../types/types";
import formValidator from "../validators/formValidator";
import { useAuth } from "../../../../../../hooks/AuthenticateContext";

interface FormValues {
  chave: string;
  valor: string;
  descricao: string;
  unidade: string;
  ativo: boolean;
}

interface Props {
  isView?: boolean;
  isEdit?: boolean;
  selectedRow?: IParametro;
  onConfirm: () => void;
  onClose: () => void;
}

const Form: React.FC<Props> = (props: Props) => {
  const [loading, setLoading] = useState<boolean>(false);
  const { user } = useAuth();

  const handleSubmit = useCallback(async (values: FormValues, row?: IParametro) => {
    try {
      setLoading(true);
      
      const body = {
        id_parametro: row?.id_parametro,
        chave: values.chave,
        valor: values.valor,
        descricao: values.descricao,
        unidade: values.unidade,
        ativo: values.ativo,
        id_usuario_historico: user?.id,
      };

      if (props.isEdit) {
        await api.post("/editar/configuracaoParametros", body);
      } else {
        await api.post("/cadastrar/configuracaoParametros", body);
      }

      setLoading(false);
      props.onConfirm();
    } catch {
      setLoading(false);
      props.onConfirm();
    }
  }, [props]);

  const onLoadFormValues = useCallback((row: IParametro) => {
    if (row) {
      formik.setFieldValue("chave", row.chave);
      formik.setFieldValue("valor", row.valor);
      formik.setFieldValue("descricao", row.descricao);
      formik.setFieldValue("unidade", row.unidade);
      formik.setFieldValue("ativo", row.ativo);
    }
  }, []);

  const initialValues: FormValues = {
    chave: "",
    valor: "",
    descricao: "",
    unidade: "",
    ativo: true,
  };

  const formik = useFormik({
    initialValues,
    validationSchema: formValidator,
    onSubmit: (values: FormValues) => handleSubmit(values, props.selectedRow),
  });

  useEffect(() => {
    if ((props.isView || props.isEdit) && props.selectedRow) {
      onLoadFormValues(props.selectedRow);
    }
  }, [props.isView, props.isEdit, props.selectedRow, onLoadFormValues]);

  const unidadeOptions = [
    { value: "UNIDADES", label: "UNIDADES" },
    { value: "HORAS", label: "HORAS" },
    { value: "MINUTOS", label: "MINUTOS" },
    { value: "SEGUNDOS", label: "SEGUNDOS" },
  ];

  return (
    <>
      <Loading loading={loading} skeletonFields={5} skeletonColumns={2} />
      <div className="grid grid-cols-2 gap-3 mb-4 p-5">
        <div>
            <InputCustom
                id="chave"
                title="Chave"
                placeholder="Informe a chave"
                onChange={formik.handleChange("chave")}
                touched={formik.touched.chave}
                error={formik.errors.chave}
                value={formik.values.chave}
                disabled={props.isView}
            />
        </div>
        <div>
            <InputCustom
                id="valor"
                title="Valor"
                placeholder="Informe o valor"
                onChange={formik.handleChange("valor")}
                touched={formik.touched.valor}
                error={formik.errors.valor}
                value={formik.values.valor}
                disabled={props.isView}
            />
        </div>
        <div className="col-span-2">
            <InputCustom
                id="descricao"
                title="Descrição"
                placeholder="Informe a descrição"
                onChange={formik.handleChange("descricao")}
                touched={formik.touched.descricao}
                error={formik.errors.descricao}
                value={formik.values.descricao}
                disabled={props.isView}
            />
        </div>
        <div>
            <SelectCustom
                id="unidade"
                title="Unidade"
                data={unidadeOptions}
                onChange={(e: { value: string }) => formik.setFieldValue("unidade", e?.value)}
                touched={formik.touched.unidade}
                error={formik.errors.unidade}
                value={formik.values.unidade}
                disabled={props.isView}
            />
        </div>
        <div className="flex items-center mt-6">
            <Checkbox
                title="Ativo"
                checked={formik.values.ativo}
                onChecked={() => {
                    if (!props.isView) {
                        formik.setFieldValue("ativo", !formik.values.ativo)
                    }
                }}
            />
        </div>
      </div>
      <div className="w-full h-14 flex items-center justify-end bg-[#FFFFFF] shadow-xl">
        <button
          type="button"
          className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#F9FAFA] text-sm text-[#000000] font-bold rounded-full mr-2"
          style={{ border: "1px solid #DBDEDF" }}
          onClick={props.onClose}
          id="btnCancel"
        >
          Cancelar
        </button>
        {!props.isView && (
            <button
            type="button"
            className="w-24 h-9 pl-3 pr-3 flex items-center justify-center bg-[#0A4984] text-sm text-[#fff] font-bold rounded-full mr-2"
            onClick={() => formik.handleSubmit()}
            id="btnSave"
            >
            Salvar
            </button>
        )}
      </div>
    </>
  );
};

export default Form;
